import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Plus, Save, Trash2, X } from 'lucide-react';
import { RichTextEditor } from './RichTextEditor';
import { toast } from 'sonner';
import { createFAQ, updateFAQ, deleteFAQ } from '../services/api';

export const FAQManager = ({ faqs, onFaqsChange }) => {
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({ question: '', answer: '' });

  const handleAddFAQ = async () => {
    if (!formData.question || !formData.answer) {
      toast.error('Please fill in both question and answer');
      return;
    }

    try {
      const newFaq = {
        id: Date.now().toString(),
        question: formData.question,
        answer: formData.answer
      };
      await createFAQ(newFaq);
      onFaqsChange([...faqs, newFaq]);
      setFormData({ question: '', answer: '' });
      setShowAddForm(false);
      toast.success('FAQ added successfully');
    } catch (error) {
      toast.error('Failed to add FAQ');
      console.error(error);
    }
  };

  const handleUpdateFAQ = async (id) => {
    const faq = faqs.find(f => f.id === id);
    if (!faq) return;

    try {
      await updateFAQ(id, faq);
      setEditingId(null);
      toast.success('FAQ updated successfully');
    } catch (error) {
      toast.error('Failed to update FAQ');
      console.error(error);
    }
  };

  const handleDeleteFAQ = async (id) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;

    try {
      await deleteFAQ(id);
      onFaqsChange(faqs.filter(f => f.id !== id));
      toast.success('FAQ deleted successfully');
    } catch (error) {
      toast.error('Failed to delete FAQ');
      console.error(error);
    }
  };

  const updateFaqField = (id, field, value) => {
    onFaqsChange(faqs.map(faq => 
      faq.id === id ? { ...faq, [field]: value } : faq
    ));
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold dark:text-white">Manage FAQs</h3>
        <Button
          onClick={() => setShowAddForm(!showAddForm)}
          className="bg-otter-green hover:bg-otter-green-600 text-white"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add FAQ
        </Button>
      </div>

      {showAddForm && (
        <Card className="dark:bg-gray-800 dark:border-gray-700">
          <CardContent className="p-6 space-y-4">
            <div>
              <Label>Question</Label>
              <Input
                value={formData.question}
                onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                placeholder="Enter question"
              />
            </div>
            <div>
              <Label>Answer</Label>
              <RichTextEditor
                value={formData.answer}
                onChange={(value) => setFormData({ ...formData, answer: value })}
                placeholder="Enter answer"
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleAddFAQ} className="bg-otter-green hover:bg-otter-green-600 text-white">
                <Save className="h-4 w-4 mr-2" />
                Add FAQ
              </Button>
              <Button variant="outline" onClick={() => {
                setShowAddForm(false);
                setFormData({ question: '', answer: '' });
              }}>
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="space-y-4">
        {faqs.map((faq) => (
          <Card key={faq.id} className="dark:bg-gray-800 dark:border-gray-700">
            <CardContent className="p-6">
              {editingId === faq.id ? (
                <div className="space-y-4">
                  <div>
                    <Label>Question</Label>
                    <Input
                      value={faq.question}
                      onChange={(e) => updateFaqField(faq.id, 'question', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Answer</Label>
                    <RichTextEditor
                      value={faq.answer}
                      onChange={(value) => updateFaqField(faq.id, 'answer', value)}
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={() => handleUpdateFAQ(faq.id)}
                      className="bg-otter-green hover:bg-otter-green-600 text-white"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save
                    </Button>
                    <Button variant="outline" onClick={() => setEditingId(null)}>
                      <X className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-lg dark:text-white">{faq.question}</h4>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setEditingId(faq.id)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteFAQ(faq.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div 
                    className="text-gray-600 dark:text-gray-400"
                    dangerouslySetInnerHTML={{ __html: faq.answer }}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};
